import { FC, ReactNode } from 'react';
import { createUseStyles } from 'react-jss';
import { BRAND, CHART_INK } from './agencyChartTheme';

const useStyles = createUseStyles({
    card: {
        padding: '16px 16px 18px',
        borderRadius: 12,
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'var(--ion-background-color)'
    },

    head: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        marginBottom: 4,
        fontSize: 15,
        fontWeight: 600,
        color: BRAND.deepTeal
    },

    // A blurb carries most of the gap below the heading, so a card without one has to carry it
    // itself or it reads noticeably tighter than every other card on the page.
    headAlone: {
        marginBottom: 18
    },

    heading: {
        flex: 1,
        minWidth: 0
    },

    // A short coral rule against each heading: enough of the brand to place the page, small enough
    // that it never competes with the marks in the chart underneath.
    headMark: {
        width: 3,
        height: 15,
        borderRadius: 2,
        background: BRAND.coral
    },

    blurb: {
        marginBottom: 14,
        fontSize: 12.5,
        lineHeight: 1.5,
        color: CHART_INK.label
    }
});

/** One panel of the Insights tab: a branded heading, optionally a line saying what it answers, then the thing. */
export const InsightsCard: FC<{
    heading: string;
    /** Omitted by a panel whose heading already says everything it has to say. */
    blurb?: string;
    /** Sits at the far end of the heading row. For the mark that explains how to read the panel. */
    action?: ReactNode;
    children: ReactNode;
}> = ({ heading, blurb, action, children }) => {
    const classes = useStyles();

    return (
        <div className={classes.card}>
            <div className={`${classes.head}${blurb ? '' : ` ${classes.headAlone}`}`}>
                <span className={classes.headMark} />
                <span className={classes.heading}>{heading}</span>
                {action}
            </div>
            {blurb && <div className={classes.blurb}>{blurb}</div>}
            {children}
        </div>
    );
};
